/**
 * Proof Trail Component
 *
 * Displays verifiable chains of evidence:
 * Before → Removal → Fabrication → Installation → Final
 *
 * Each step is:
 * - Timestamped
 * - Confidence-scored
 * - Linked to images
 *
 * Voice trigger: "Prove we replaced the floor pans"
 * Output: Visual chain + optional PDF export
 */

import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import type { ProofTrail, ProofStep } from '@/types/memory';
import type { ImageMetadata, ConditionState } from '@/types';
import { generateEvidenceId } from '@/services/memory';

interface ProofTrailViewProps {
  trail: ProofTrail;
  images: Map<string, ImageMetadata>;
  onStepClick: (step: ProofStep) => void;
  onImageClick: (imageId: string) => void;
  onExport?: () => void;
  onClose: () => void;
  className?: string;
}

// Visual indicators for each step type
const STEP_STYLES: Record<string, { icon: string; color: string; bgColor: string }> = {
  'Before': { icon: '📷', color: 'text-amber-400', bgColor: 'bg-amber-500/20' },
  'Removal': { icon: '🔧', color: 'text-red-400', bgColor: 'bg-red-500/20' },
  'Stripped': { icon: '⚙️', color: 'text-orange-400', bgColor: 'bg-orange-500/20' },
  'Fabrication/Repair': { icon: '🔨', color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
  'Installation': { icon: '🔩', color: 'text-emerald-400', bgColor: 'bg-emerald-500/20' },
  'Final': { icon: '✅', color: 'text-green-400', bgColor: 'bg-green-500/20' },
};

export function ProofTrailView({
  trail,
  images,
  onStepClick,
  onImageClick,
  onExport,
  onClose,
  className,
}: ProofTrailViewProps) {
  const [selectedStep, setSelectedStep] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleStepClick = useCallback((step: ProofStep) => {
    setSelectedStep(step.id);
    onStepClick(step);
  }, [onStepClick]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      await onExport?.();
    } finally {
      setIsExporting(false);
    }
  }, [onExport]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={clsx('fixed inset-0 z-[70] bg-workshop-bg', className)}
    >
      <div className="h-full flex flex-col">
        {/* Header */}
        <header className="flex items-center justify-between px-6 py-4 border-b border-workshop-border bg-workshop-bg">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-workshop-surface transition-colors"
            >
              <BackIcon className="w-5 h-5 text-workshop-text-muted" />
            </button>
            <div>
              <h1 className="text-lg text-white font-semibold">{trail.title}</h1>
              <p className="text-sm text-workshop-text-muted">
                {trail.steps.length} steps • {trail.totalImages} images
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Confidence indicator */}
            <div className={clsx(
              'flex items-center gap-2 px-3 py-1.5 rounded-lg',
              trail.confidence >= 90 ? 'bg-green-500/10 text-green-400' :
              trail.confidence >= 70 ? 'bg-amber-500/10 text-amber-400' : 'bg-red-500/10 text-red-400'
            )}>
              <div className={clsx(
                'w-2 h-2 rounded-full',
                trail.confidence >= 90 ? 'bg-green-500' :
                trail.confidence >= 70 ? 'bg-amber-500' : 'bg-red-500'
              )} />
              <span className="text-sm font-medium">
                {trail.confidence}%
              </span>
            </div>

            {/* Export button */}
            {onExport && (
              <button
                onClick={handleExport}
                disabled={isExporting}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  'bg-workshop-surface text-white hover:bg-workshop-border',
                  isExporting && 'opacity-50 cursor-not-allowed'
                )}
              >
                {isExporting ? 'Exporting...' : 'Export'}
              </button>
            )}
          </div>
        </header>

        {/* Main content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-5xl mx-auto">
            {/* Trail visualization */}
            <div className="relative">
              {/* Steps */}
              <div className="space-y-3">
                {trail.steps.map((step, index) => {
                  const style = STEP_STYLES[step.label] || STEP_STYLES['Before'];
                  const primaryImage = step.primaryImageId ? images.get(step.primaryImageId) : null;
                  const evidenceId = generateEvidenceId(index);
                  const isSelected = selectedStep === step.id;

                  return (
                    <motion.div
                      key={step.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={clsx(
                        'relative flex gap-4 p-3 rounded-lg cursor-pointer transition-all',
                        isSelected ? 'bg-workshop-surface ring-2 ring-workshop-accent' : 'hover:bg-workshop-surface/50'
                      )}
                      onClick={() => handleStepClick(step)}
                    >
                      {/* Step indicator - show thumbnail if available, otherwise icon */}
                      <div className={clsx(
                        'relative z-10 w-14 h-14 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0',
                        !primaryImage && style.bgColor
                      )}>
                        {primaryImage ? (
                          <img
                            src={primaryImage.thumbnailUrl}
                            alt={step.label}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-xl">{style.icon}</span>
                        )}
                      </div>

                      {/* Step content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={clsx('font-semibold', style.color)}>
                            {step.label}
                          </h3>
                          <span className="px-1.5 py-0.5 text-xs font-mono bg-workshop-bg/50 rounded text-workshop-text-muted">
                            {evidenceId}
                          </span>
                          <span className="text-xs text-workshop-text-muted">
                            {new Date(step.timestamp).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                          {/* Compact confidence indicator */}
                          <span className={clsx(
                            'ml-auto text-xs font-mono',
                            step.confidence >= 90 ? 'text-green-500' :
                            step.confidence >= 70 ? 'text-amber-500' : 'text-red-500'
                          )}>
                            {step.confidence}%
                          </span>
                        </div>

                        <p className="text-sm text-workshop-text-muted">
                          {step.description}
                        </p>

                        {/* Image thumbnails - only show if more than 1 image */}
                        {step.imageIds.length > 1 && (
                          <div className="flex gap-1.5 mt-2 overflow-x-auto">
                            {step.imageIds.slice(0, 5).map((imageId, imgIndex) => {
                              const img = images.get(imageId);
                              if (!img) return null;

                              return (
                                <motion.button
                                  key={imageId}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onImageClick(imageId);
                                  }}
                                  className="relative flex-shrink-0 w-12 h-12 rounded overflow-hidden group"
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  <img
                                    src={img.thumbnailUrl}
                                    alt={img.componentDetail}
                                    className="w-full h-full object-cover"
                                  />
                                  {imgIndex === 0 && step.primaryImageId === imageId && (
                                    <div className="absolute inset-0 ring-2 ring-workshop-accent ring-inset" />
                                  )}
                                </motion.button>
                              );
                            })}
                            {step.imageIds.length > 5 && (
                              <div className="flex-shrink-0 w-12 h-12 rounded bg-workshop-surface flex items-center justify-center">
                                <span className="text-xs text-workshop-text-muted">
                                  +{step.imageIds.length - 5}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Summary footer */}
            <div className="mt-8 p-4 bg-workshop-surface rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-workshop-text-muted">Date Range</p>
                  <p className="text-white">
                    {new Date(trail.dateRange.start).toLocaleDateString()} —{' '}
                    {new Date(trail.dateRange.end).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-workshop-text-muted">Status</p>
                  <p className={trail.isComplete ? 'text-green-400' : 'text-amber-400'}>
                    {trail.isComplete ? 'Complete Evidence Chain' : 'Partial Evidence'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Compact proof trail card for lists
 */
export function ProofTrailCard({
  trail,
  onClick,
  className,
}: {
  trail: ProofTrail;
  onClick: () => void;
  className?: string;
}) {
  return (
    <motion.button
      onClick={onClick}
      className={clsx(
        'w-full p-4 rounded-lg bg-workshop-surface text-left transition-all',
        'hover:bg-workshop-surface/80 hover:ring-1 hover:ring-workshop-border',
        className
      )}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
    >
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-semibold text-white">{trail.title}</h3>
        <div className={clsx(
          'px-2 py-0.5 rounded text-xs font-medium',
          trail.isComplete ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'
        )}>
          {trail.isComplete ? 'Complete' : 'Partial'}
        </div>
      </div>

      <p className="text-sm text-workshop-text-muted mb-3">{trail.component}</p>

      {/* Mini step visualization */}
      <div className="flex items-center gap-1 mb-3">
        {trail.steps.map((step, i) => {
          const style = STEP_STYLES[step.label] || STEP_STYLES['Before'];
          return (
            <div key={step.id} className="flex items-center">
              <div className={clsx(
                'w-6 h-6 rounded-full flex items-center justify-center text-xs',
                style.bgColor
              )}>
                {step.hasEvidence ? '✓' : '?'}
              </div>
              {i < trail.steps.length - 1 && (
                <div className="w-4 h-0.5 bg-workshop-border" />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-4 text-xs text-workshop-text-muted">
        <span>{trail.steps.length} steps</span>
        <span>{trail.totalImages} images</span>
        <span>{trail.confidence}% confidence</span>
      </div>
    </motion.button>
  );
}

/**
 * Proof search result - what Ventura shows after "Prove we replaced..."
 */
export function ProofSearchResult({
  trail,
  images,
  onViewFull,
  className,
}: {
  trail: ProofTrail;
  images: Map<string, ImageMetadata>;
  onViewFull: () => void;
  className?: string;
}) {
  const beforeStep = trail.steps.find(s => s.label === 'Before');
  const afterStep = trail.steps.find(s => s.label === 'Final') || trail.steps[trail.steps.length - 1];

  const beforeImage = beforeStep?.primaryImageId ? images.get(beforeStep.primaryImageId) : null;
  const afterImage = afterStep?.primaryImageId ? images.get(afterStep.primaryImageId) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={clsx('bg-workshop-surface rounded-lg overflow-hidden', className)}
    >
      {/* Header */}
      <div className="p-4 border-b border-workshop-border">
        <div className="flex items-center gap-2">
          <span className="text-lg">✅</span>
          <h3 className="font-semibold text-white">Proof Found</h3>
        </div>
        <p className="text-sm text-workshop-text-muted mt-1">{trail.title}</p>
      </div>

      {/* Before/After comparison */}
      <div className="grid grid-cols-2 gap-px bg-workshop-border">
        <div className="bg-workshop-surface p-4">
          <p className="text-xs text-amber-400 font-medium mb-2">BEFORE</p>
          {beforeImage ? (
            <div className="aspect-video relative rounded overflow-hidden">
              <img
                src={beforeImage.thumbnailUrl}
                alt="Before"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 rounded text-xs text-white font-mono">
                {generateEvidenceId(0)}
              </div>
            </div>
          ) : (
            <div className="aspect-video bg-workshop-bg rounded flex items-center justify-center">
              <span className="text-workshop-text-muted">No image</span>
            </div>
          )}
        </div>

        <div className="bg-workshop-surface p-4">
          <p className="text-xs text-green-400 font-medium mb-2">AFTER</p>
          {afterImage ? (
            <div className="aspect-video relative rounded overflow-hidden">
              <img
                src={afterImage.thumbnailUrl}
                alt="After"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 rounded text-xs text-white font-mono">
                {generateEvidenceId(trail.steps.length - 1)}
              </div>
            </div>
          ) : (
            <div className="aspect-video bg-workshop-bg rounded flex items-center justify-center">
              <span className="text-workshop-text-muted">No image</span>
            </div>
          )}
        </div>
      </div>

      {/* Footer with action */}
      <div className="p-4 flex items-center justify-between">
        <div className="text-sm text-workshop-text-muted">
          {trail.steps.length} steps • {trail.confidence}% confidence
        </div>
        <button
          onClick={onViewFull}
          className="px-4 py-2 bg-workshop-accent text-white rounded-lg text-sm font-medium hover:bg-workshop-accent/90 transition-colors"
        >
          View Full Proof Trail
        </button>
      </div>
    </motion.div>
  );
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

export default ProofTrailView;
